/* eslint-disable @typescript-eslint/no-explicit-any */
import { deepCopy } from '../../helpers';

/**
 * Converts legacy island types to new types based on size
 * - STANDARD → GRILL/BAR/SMOKER based on height
 * - SMOKER → ASADO if size matches ASADO defaults (height: 16, width: 30)
 */
const migrateIslandType = (island: any): string => {
	const currentType = island.type;
	const height = island.size?.height ?? island.panels?.children?.[0]?.size?.height ?? 34.5;
	const width = island.size?.width ?? island.panels?.children?.[0]?.size?.width ?? 60;

	// Convert SMOKER to ASADO if it matches ASADO default size
	if (currentType === 'SMOKER' && height === 16 && width === 30) {
		return 'ASADO';
	}

	// Convert STANDARD to appropriate type based on height
	if (currentType === 'STANDARD') {
		if (height >= 36.75) {
			return 'BAR';
		} else if (height >= 27.5) {
			return 'GRILL';
		} else {
			return 'SMOKER';
		}
	}

	return currentType;
};

/**
 * Schema update from version 8 to version 9
 * - Adds obstructions support and backsplash panel structure
 * - Migrates legacy island types (STANDARD → GRILL/BAR/SMOKER, SMOKER → ASADO)
 */
export const u9 = (blueprint: any): any => {
	console.log('schema-update:process-update:9:started');

	try {
		const updatedBlueprint = deepCopy(blueprint);
		updatedBlueprint.schemaVersion = 9;

		// Ensure kitchens have obstructions in design
		if (updatedBlueprint.kitchens?.children) {
			updatedBlueprint.kitchens.children = updatedBlueprint.kitchens.children.map((kitchen: any) => {
				// Ensure obstructions exist in design
				if (!kitchen.design.obstructions) {
					kitchen.design.obstructions = {
						defaultIslandId: null,
						children: []
					};
				}

				// Migrate island types
				if (kitchen.design.islands?.children?.length > 0) {
					kitchen.design.islands.children = kitchen.design.islands.children.map((island: any) => {
						const newType = migrateIslandType(island);
						if (newType !== island.type) {
							console.log(`schema-update:9:migrated island type from ${island.type} to ${newType}`);
						}
						return {
							...island,
							type: newType
						};
					});
				}

				// Update obstruction structure if obstructions exist
				if (kitchen.design.obstructions?.children?.length > 0) {
					kitchen.design.obstructions.children = kitchen.design.obstructions.children.map((obstruction: any) => {
						return {
							...obstruction,
							// Ensure trims exists (for panelTrims)
							trims: obstruction.trims ?? { children: [] },
							// Ensure panel structure is correct
							panel: obstruction.panel ?? null
						};
					});
				}

				// Update countertop backsplashes structure if countertops exist
				if (kitchen.design.countertops?.children?.length > 0) {
					kitchen.design.countertops.children = kitchen.design.countertops.children.map((countertop: any) => {
						return {
							...countertop,
							// Ensure backsplashes exist
							backsplashes: countertop.backsplashes ?? { children: [], frontPanelId: null, surroundMaterial: null }
						};
					});
				}

				return kitchen;
			});
		}

		console.log('schema-update:process-update:9:completed');
		return updatedBlueprint;
	} catch (err) {
		console.error('schema-update:process-update:9:error', err);
		return null;
	}
};
